import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { HotkeysService } from '@helios/hotkeys'
import {
  RootState,
  selectActiveSession,
  selectSessions,
  Session,
  SessionWithState,
  setFocusedSubSleeveAccountId,
  setIsSwitchingSession,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import _ from 'lodash-es'
import { Subject } from 'rxjs'
import { distinctUntilChanged, map, takeUntil, tap } from 'rxjs/operators'
import {
  MAX_ALLOWED_SESSIONS,
  MAX_PINNED_SESSIONS,
  SessionsService,
} from '../../services/sessions-service/sessions.service'

@Component({
  selector: 'hs-sessions-drawer',
  templateUrl: './sessions-drawer.component.html',
  styleUrls: ['./sessions-drawer.component.scss'],
})
export class SessionsDrawerComponent implements OnInit, OnDestroy {
  @Output() closePanel = new EventEmitter<void>()
  @Input() isVisible = false

  /** @internal */
  pinnedSessions: SessionWithState[] = []
  unpinnedSessions: SessionWithState[] = []

  /** @internal */
  sessionsAtMax = false
  activeSessionId?: string
  deleteLoading = false
  deleteSessionId?: string
  isLoadingSessions = true
  disableLoadingSessionsComplete = false
  isRefreshingSession = false
  maxAllowedSessions = MAX_ALLOWED_SESSIONS
  maxPinnedSessionsAllowed = MAX_PINNED_SESSIONS
  expandedCards: Record<string, boolean> = {}

  private readonly destroySubject = new Subject<void>()

  constructor(
    private readonly store: Store<RootState>,
    private readonly sessionService: SessionsService,
    hotkeysService: HotkeysService<
      'global-create-session' | 'global-delete-active-session'
    >,
  ) {
    hotkeysService.bind('global-create-session', () => {
      this.toggleCreateNewSessionModal()
    })

    hotkeysService.bind('global-delete-active-session', () => {
      this.deleteActivatedSession()
    })
  }

  ngOnInit() {
    this.store
      .select(selectSessions)
      .pipe(
        takeUntil(this.destroySubject),
        distinctUntilChanged((a, b) => _.isEqual(a, b)),
        tap(nextSessions => {
          this.pinnedSessions = nextSessions.filter(x => x.isPinned)
          this.unpinnedSessions = nextSessions.filter(x => !x.isPinned)
          this.sessionsAtMax = nextSessions.length >= MAX_ALLOWED_SESSIONS
          this.sortSessions()
        }),
      )
      .subscribe()

    this.store
      .select(selectActiveSession)
      .pipe(
        takeUntil(this.destroySubject),
        map(x => x?.id),
        distinctUntilChanged(),
      )
      .subscribe(id => {
        this.activeSessionId = id
      })

    this.sessionService.isSessionsLoaded.subscribe(isLoaded => {
      this.isLoadingSessions = !isLoaded
    })
  }

  ngOnDestroy() {
    this.destroySubject.next()
    this.destroySubject.complete()
  }

  sortSessions() {
    this.expandedCards = {}
    this.pinnedSessions = _.sortBy(this.pinnedSessions, x => x.createdDateTime)
    this.unpinnedSessions = _.sortBy(
      this.unpinnedSessions,
      x => x.createdDateTime,
    )
  }

  async deleteSession(sessionId: string) {
    this.deleteLoading = true
    this.deleteSessionId = sessionId
    await this.sessionService.checkForUncommittedCommandsAndCloseSession(
      sessionId,
    )

    this.deleteLoading = false
    this.deleteSessionId = undefined
  }

  async makeSessionActive(session: Session) {
    if (session != null) {
      this.store.dispatch(setIsSwitchingSession({ isSwitching: true }))
      await this.sessionService.activateSession(session.id)
      this.store.dispatch(
        setFocusedSubSleeveAccountId({ subSleeveAccountId: undefined }),
      )
      this.store.dispatch(setIsSwitchingSession({ isSwitching: false }))
    }
  }

  async deleteActivatedSession() {
    if (this.sessionService.activeSession) {
      await this.sessionService.close(this.sessionService.activeSession.id)
    }
  }

  async toggleCreateNewSessionModal() {
    this.isLoadingSessions = true
    let session: Session | undefined
    const accountIds =
      session?.loadedAccountIds == null ? [] : session.loadedAccountIds

    try {
      const newSession = await this.sessionService.createFromModal(accountIds)
      if (!newSession) this.disableLoadingSessionsComplete = true
    } catch {
      this.disableLoadingSessionsComplete = false
    }

    this.isLoadingSessions = false
  }

  async refreshSession(session: Session) {
    this.isRefreshingSession = true
    await this.sessionService.refreshSession(session)
    this.isRefreshingSession = false
  }
}
